import type { CandidateDiversityToken, ProgramCandidatesFilters } from './program-candidates-query';
import type { CandidatePhaseStatus, CandidateSubscriptionStatus } from './program-management-model';

/**
 * EU-593 — estado do drawer "Filtrar candidatos" (11 campos, ordem RN-20).
 * Chips carregam id + label resolvidos pelo autocomplete; `toQueryFilters`
 * projeta só os ids para a query server-side.
 */

export interface CandidateFilterChip {
  id: number;
  label: string;
}

export interface CandidateLocationChip {
  /** UF inteira ou cidade específica ("cidades ou estados", GPR-041). */
  kind: 'state' | 'city';
  /** id da cidade (kind=city). */
  cityId?: number;
  /** UF de 2 letras (kind=state). */
  stateAcronym?: string;
  label: string;
}

export interface CandidateFiltersState {
  subscriptionStatus: CandidateSubscriptionStatus | null;
  phaseStatus: CandidatePhaseStatus | null;
  qualified: boolean | null;
  apt: boolean | null;
  locations: CandidateLocationChip[];
  courseTypes: CandidateFilterChip[];
  courseAreas: CandidateFilterChip[];
  courseGroups: CandidateFilterChip[];
  /** Datas civis YYYY-MM-DD; '' = não preenchida. */
  formationDateMin: string;
  formationDateMax: string;
  diversity: CandidateDiversityToken[];
}

export const EMPTY_CANDIDATE_FILTERS: CandidateFiltersState = {
  subscriptionStatus: null,
  phaseStatus: null,
  qualified: null,
  apt: null,
  locations: [],
  courseTypes: [],
  courseAreas: [],
  courseGroups: [],
  formationDateMin: '',
  formationDateMax: '',
  diversity: [],
};

/** Habilita "Aplicar filtro" / mostra "Limpar filtro(s)" (GPR-038/048). */
export function hasAnyCandidateFilter(state: CandidateFiltersState): boolean {
  return (
    state.subscriptionStatus !== null ||
    state.phaseStatus !== null ||
    state.qualified !== null ||
    state.apt !== null ||
    state.locations.length > 0 ||
    state.courseTypes.length > 0 ||
    state.courseAreas.length > 0 ||
    state.courseGroups.length > 0 ||
    state.formationDateMin !== '' ||
    state.formationDateMax !== '' ||
    state.diversity.length > 0
  );
}

/** Projeta o estado do drawer nos parâmetros server-side da listagem. */
export function toQueryFilters(state: CandidateFiltersState): ProgramCandidatesFilters {
  const cityIds = state.locations
    .filter((l) => l.kind === 'city' && l.cityId !== undefined)
    .map((l) => l.cityId as number);
  const stateAcronyms = state.locations
    .filter((l) => l.kind === 'state' && l.stateAcronym)
    .map((l) => l.stateAcronym as string);
  return {
    subscriptionStatus: state.subscriptionStatus ?? undefined,
    phaseStatus: state.phaseStatus ?? undefined,
    qualified: state.qualified ?? undefined,
    apt: state.apt ?? undefined,
    cityIds: cityIds.length > 0 ? cityIds : undefined,
    stateAcronyms: stateAcronyms.length > 0 ? stateAcronyms : undefined,
    courseTypeIds: state.courseTypes.length > 0 ? state.courseTypes.map((c) => c.id) : undefined,
    courseAreaIds: state.courseAreas.length > 0 ? state.courseAreas.map((c) => c.id) : undefined,
    courseGroupIds: state.courseGroups.length > 0 ? state.courseGroups.map((c) => c.id) : undefined,
    diversity: state.diversity.length > 0 ? state.diversity : undefined,
    formationDateMin: state.formationDateMin || undefined,
    formationDateMax: state.formationDateMax || undefined,
  };
}

/** Opções EXATAS do campo Critérios de diversidade (GPR-045). */
export const DIVERSITY_FILTER_OPTIONS: { value: CandidateDiversityToken; label: string }[] = [
  { value: 'mulheres', label: 'Mulheres (CIS ou Trans)' },
  { value: 'transexuais', label: 'Pessoas transexuais' },
  { value: 'amarelas', label: 'Pessoas amarelas' },
  { value: 'indigenas', label: 'Pessoas indígenas' },
  { value: 'pardas', label: 'Pessoas pardas' },
  { value: 'pretas', label: 'Pessoas pretas' },
  { value: 'pessoas-com-deficiencia', label: 'Pessoas com deficiência' },
];
